// Panneau « Mises à jour » de la fiche appareil — Windows (KB) et Linux (paquets).
//
// Les deux plateformes partagent la même route serveur
// (POST /api/platform/updates/{id}/install) : c'est la plateforme qui traduit en
// commande Windows Update ou apt/dnf. Ce composant ne décide donc que de ce qu'il
// affiche et de ce qu'il envoie comme mode.

import { useState } from 'react';
import { Power, RefreshCw, ShieldAlert, RotateCcw, Package } from 'lucide-react';
import { api } from '../lib/api.jsx';

const tableCls = 'w-full border-collapse text-xs';
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule';
const tdCls = 'px-3 py-2 text-fg-2 border-b border-sidebar';

const primaryBtn =
  'bg-blue-500 border-none rounded-lg px-4 py-2 text-white text-[11px] font-bold disabled:opacity-60 cursor-pointer hover:bg-blue-600';
const secondaryBtn =
  'bg-rule border border-rule-3 rounded-lg px-4 py-2 text-fg-2 text-[11px] font-bold disabled:opacity-60 cursor-pointer hover:bg-rule-2';
const ghostBtn =
  'bg-transparent border border-rule-3 rounded-lg px-3 py-2 text-muted text-[11px] cursor-pointer';

/** Étiquette de sévérité d'une mise à jour, selon la plateforme. */
const UpdateSeverity = ({ update, isLinux }) => {
  if (isLinux) {
    return update.isSecurity ? (
      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-crit/20 text-red-300">Sécurité</span>
    ) : (
      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-500/15 text-muted">Standard</span>
    );
  }
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
        update.severity === 'Critical'
          ? 'bg-crit/20 text-red-300'
          : update.severity === 'Important'
            ? 'bg-warn/20 text-amber-300'
            : 'bg-slate-500/15 text-muted'
      }`}
    >
      {update.severity || 'N/A'}
    </span>
  );
};

export function SystemUpdatesPanel({ agentId, platform, inv, sectionCls, sTitleCls, canInstall = true }) {
  const isLinux = platform === 'linux';
  const updates = inv?.pendingUpdates || [];
  const count = inv?.pendingUpdatesCount ?? 0;
  const securityUpdates = updates.filter((u) => u.isSecurity);
  const rebootRequired = !!inv?.rebootRequired;
  const packageManager = inv?.packageManagerName || '';

  const [busy, setBusy] = useState(null); // le mode en cours d'envoi
  const [message, setMessage] = useState(null);
  const [selected, setSelected] = useState([]);
  // Redémarrage planifié, optionnel. Par défaut ce soir 22 h locale : une
  // fenêtre plausible, que le technicien ajuste — proposer « maintenant »
  // inviterait à redémarrer un poste en pleine journée.
  const [rebootEnabled, setRebootEnabled] = useState(false);
  const [rebootLocal, setRebootLocal] = useState(() => {
    const d = new Date();
    d.setHours(22, 0, 0, 0);
    if (d <= new Date()) d.setDate(d.getDate() + 1);
    // Format attendu par <input type="datetime-local"> : heure LOCALE, sans fuseau.
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  const rebootInvalid = rebootEnabled && (!rebootLocal || new Date(rebootLocal) <= new Date());

  // Clé de sélection : le nom de paquet sous Linux, l'identifiant KB sous
  // Windows. C'est aussi ce que le serveur attend dans `packages` / `kbIds`.
  const keyOf = (u) => (isLinux ? u.package : u.kbArticleId ? 'KB' + u.kbArticleId : '');
  const selectable = updates.map(keyOf).filter(Boolean);

  const send = async (mode, body, label) => {
    if (rebootInvalid) {
      setMessage({ type: 'error', text: 'La date de redémarrage doit être dans le futur.' });
      return;
    }
    setBusy(mode);
    setMessage(null);
    try {
      // Le champ datetime-local est en heure LOCALE ; le serveur raisonne en UTC.
      // toISOString() fait la conversion — l'omettre décalerait le redémarrage
      // de plusieurs heures selon le fuseau.
      const payload = rebootEnabled
        ? { ...body, rebootAt: new Date(rebootLocal).toISOString() }
        : body;
      const r = await api.post('/platform/updates/' + agentId + '/install', payload);
      setMessage(
        r
          ? { type: 'success', text: r.message || `Commande envoyée (${label})` }
          : { type: 'error', text: "Erreur lors de l'envoi de la commande" }
      );
      if (r) setSelected([]);
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur : ' + (e.message || e) });
    }
    setBusy(null);
  };

  const runLinux = (mode) => {
    if (mode === 'dist-upgrade' &&
        !confirm(
          'Une mise à jour complète (dist-upgrade) peut installer ET supprimer des paquets ' +
            'pour satisfaire les dépendances, y compris un nouveau noyau.\n\nContinuer ?'
        ))
      return;
    send(mode, { mode, packages: mode === 'packages' ? selected : [] }, mode);
  };

  return (
    <div className={sectionCls}>
      <div className={`${sTitleCls} flex items-center`}>
        <span className="inline-flex items-center gap-2">
          <RefreshCw size={16} strokeWidth={1.75} />
          {isLinux ? 'Mises à jour de paquets' : 'Mises à jour Windows'}
          {isLinux && packageManager && (
            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-rule text-muted">
              {packageManager}
            </span>
          )}
        </span>
        <span
          className={`ml-auto text-xs font-bold font-mono ${
            count > 10 ? 'text-red-300' : count > 0 ? 'text-amber-300' : 'text-emerald-300'
          }`}
        >
          {count}
        </span>
      </div>

      {/* Un noyau ou une glibc mise à jour n'est pas active avant le redémarrage :
          la machine reste vulnérable alors que la liste des mises à jour est vide. */}
      {rebootRequired && (
        <div className="mb-3 bg-warn/10 border border-warn/30 rounded-lg px-3.5 py-2.5 flex items-start gap-2">
          <RotateCcw size={15} strokeWidth={2} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[12px]">
            <div className="font-semibold text-amber-300">Redémarrage en attente</div>
            <div className="text-fg-2">
              Des paquets déjà installés (noyau, bibliothèques système) ne seront actifs qu'après un
              redémarrage.
            </div>
          </div>
        </div>
      )}

      {updates.length === 0 ? (
        <div className="text-muted-2 text-xs">
          {isLinux && !packageManager
            ? "Aucun gestionnaire de paquets détecté sur cette machine."
            : 'Aucune mise à jour en attente'}
        </div>
      ) : (
        <div>
          {isLinux && securityUpdates.length > 0 && (
            <div className="mb-2.5 flex items-center gap-1.5 text-[11.5px] text-red-300">
              <ShieldAlert size={13} strokeWidth={2} />
              {securityUpdates.length} mise(s) à jour de sécurité en attente
            </div>
          )}

          <div className="max-h-[420px] overflow-y-auto">
            <table className={tableCls}>
              <thead>
                <tr>
                  <th className={`${thCls} w-[30px]`}>
                    <input
                      type="checkbox"
                      className="accent-blue-500 cursor-pointer"
                      checked={selectable.length > 0 && selected.length === selectable.length}
                      onChange={(e) => setSelected(e.target.checked ? selectable : [])}
                    />
                  </th>
                  {(isLinux
                    ? ['Paquet', 'Version actuelle', 'Nouvelle version', 'Dépôt', 'Type']
                    : ['Titre', 'KB', 'Sévérité', 'Date']
                  ).map((c) => (
                    <th key={c} className={thCls}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {updates.map((u, i) => {
                  const key = keyOf(u);
                  const isSelected = key && selected.includes(key);
                  return (
                    <tr key={i} className={isSelected ? 'bg-blue-500/[.08]' : 'bg-transparent'}>
                      <td className={tdCls}>
                        {key && (
                          <input
                            type="checkbox"
                            className="accent-blue-500 cursor-pointer"
                            checked={isSelected}
                            onChange={(e) =>
                              setSelected((prev) =>
                                e.target.checked ? [...prev, key] : prev.filter((k) => k !== key)
                              )
                            }
                          />
                        )}
                      </td>
                      {isLinux ? (
                        <>
                          <td className={`${tdCls} font-mono font-semibold`}>{u.package}</td>
                          <td className={`${tdCls} font-mono text-[11px] text-muted`}>
                            {u.currentVersion || '—'}
                          </td>
                          <td className={`${tdCls} font-mono text-[11px]`}>{u.newVersion || '—'}</td>
                          <td className={`${tdCls} text-[11px] text-muted truncate max-w-[160px]`}>
                            {u.repository || '—'}
                          </td>
                          <td className={tdCls}>
                            <UpdateSeverity update={u} isLinux />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className={`${tdCls} max-w-[220px] truncate`}>{u.title}</td>
                          <td className={`${tdCls} font-mono text-[11px]`}>
                            {u.kbArticleId ? 'KB' + u.kbArticleId : '—'}
                          </td>
                          <td className={tdCls}>
                            <UpdateSeverity update={u} isLinux={false} />
                          </td>
                          <td className={`${tdCls} text-[11px] font-mono`}>
                            {u.releaseDate ? new Date(u.releaseDate).toLocaleDateString('fr-FR') : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {canInstall && (
            <div className="flex gap-2 mt-3 items-center flex-wrap">
              {isLinux ? (
                <>
                  <button disabled={!!busy} onClick={() => runLinux('upgrade')} className={primaryBtn}
                    title="Met à jour les paquets sans en installer ni en supprimer d'autres (apt-get upgrade / dnf upgrade)">
                    {busy === 'upgrade' ? 'Envoi…' : 'Tout mettre à jour'}
                  </button>
                  <button disabled={!!busy} onClick={() => runLinux('dist-upgrade')} className={secondaryBtn}
                    title="Autorise l'ajout et la suppression de paquets pour satisfaire les dépendances (apt-get dist-upgrade / dnf distro-sync)">
                    {busy === 'dist-upgrade' ? 'Envoi…' : 'Mise à jour complète'}
                  </button>
                  {securityUpdates.length > 0 && (
                    <button disabled={!!busy} onClick={() => runLinux('security')}
                      className="bg-crit/15 border border-crit/35 rounded-lg px-4 py-2 text-red-300 text-[11px] font-bold disabled:opacity-60 cursor-pointer hover:bg-crit/25">
                      {busy === 'security' ? 'Envoi…' : `Sécurité uniquement (${securityUpdates.length})`}
                    </button>
                  )}
                  {selected.length > 0 && (
                    <button disabled={!!busy} onClick={() => runLinux('packages')}
                      className="bg-blue-800 border-none rounded-lg px-4 py-2 text-white text-[11px] font-bold disabled:opacity-60 cursor-pointer inline-flex items-center gap-1.5">
                      <Package size={13} strokeWidth={2} />
                      {busy === 'packages' ? 'Envoi…' : `Installer la sélection (${selected.length})`}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button disabled={!!busy} onClick={() => send('all', { kbIds: [] }, 'toutes')} className={primaryBtn}>
                    {busy === 'all' ? 'Envoi…' : 'Installer tout'}
                  </button>
                  {selected.length > 0 && (
                    <button disabled={!!busy}
                      onClick={() => send('selection', { kbIds: selected }, `${selected.length} KB`)}
                      className="bg-blue-800 border-none rounded-lg px-4 py-2 text-white text-[11px] font-bold disabled:opacity-60 cursor-pointer">
                      {busy === 'selection' ? 'Envoi…' : `Installer la sélection (${selected.length})`}
                    </button>
                  )}
                </>
              )}
              {selected.length > 0 && (
                <button onClick={() => setSelected([])} className={ghostBtn}>
                  Désélectionner
                </button>
              )}
            </div>
          )}

          {canInstall && (
            <div className="mt-3 flex items-center gap-2.5 flex-wrap text-[11.5px]">
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-muted hover:text-fg-2">
                <input
                  type="checkbox"
                  checked={rebootEnabled}
                  onChange={(e) => setRebootEnabled(e.target.checked)}
                />
                <Power size={13} strokeWidth={1.75} />
                Planifier un redémarrage
              </label>
              {rebootEnabled && (
                <>
                  <input
                    type="datetime-local"
                    value={rebootLocal}
                    onChange={(e) => setRebootLocal(e.target.value)}
                    className={`bg-sidebar border rounded-lg px-2.5 py-1.5 text-fg-2 text-[11.5px] outline-none ${
                      rebootInvalid ? 'border-crit' : 'border-rule'
                    }`}
                  />
                  <span className="text-[10.5px] text-muted-2">
                    {rebootInvalid
                      ? 'Date passée — choisir un moment à venir.'
                      : 'Heure locale. Le redémarrage part même si la mise à jour échoue.'}
                  </span>
                </>
              )}
            </div>
          )}

          {message && (
            <div
              className={`mt-2.5 px-3.5 py-2 rounded-lg text-xs font-semibold border ${
                message.type === 'success'
                  ? 'bg-ok/12 border-ok/30 text-emerald-300'
                  : 'bg-crit/12 border-crit/30 text-red-300'
              }`}
            >
              {message.text}
              {message.type === 'success' && (
                <span className="block font-normal text-fg-2 mt-1">
                  La commande est exécutée au prochain sondage de l'agent (moins de 15 s). Son résultat
                  apparaît dans l'historique des commandes.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
